// @givmo/org-console — *Leave organization*: the person's own way off the team (EP-MNT9 (c); Org
// Machinery OM-10 leg 4's self-target carve-out).
//
// ══ THE ORG REMOVAL ACT, AIMED AT YOURSELF ═════════════════════════════════════════════════════
//
// `DELETE /org/{ref}/members/{you}` — the same operation Team access's "Remove from team" calls, with
// the caller as its target. The backend admits it on the class's `leave_org` capability (who may
// leave is each class's own data, the rule its own removal door has always kept), and it delegates
// to the class's own removal writer, so the person's post-state is exactly what their class's door
// leaves. The card lives in the person's OWN section of the settings page, at its foot: leaving is
// the person's own act, never the organization's.
//
// ══ IT RENDERS WHERE THE HOST ASKS AND THE SERVER ADMITS ═══════════════════════════════════════
//
// The host hands the panel `onLeft` — its sign-out and door hop — and the caller's ONE standing read
// carries `leave_org` (a capability, never a tier: the build law). A host that wires no `onLeft`
// renders no card, because a person who has left must not be left standing on a console that can no
// longer read their organization. The card also waits for the person's own id and the
// organization's name: the act addresses the one, the words name the other.
//
// ══ ONE REFUSAL HAS ITS OWN SENTENCE ═══════════════════════════════════════════════════════════
//
// The organization's last Global Admin cannot leave — the class writer's own last-Owner guard,
// translated by the console into 422 `LAST_GLOBAL_ADMIN_REFUSED`. After that refusal the dialog's
// buttons give way to a single Close (D-EP-62): leaving again could only be refused again, as the
// connected-AI dialog does once nothing is left to do. Every other failure is the generic line, with
// the confirm still offered, and nothing changed. On success the host takes over at once; the
// confirm stays busy until it does.
//
// DATA CONTRACT: the `org-leave-*` test-id family (fixed, not prefixed by the mount): `-card`, `-open`,
// `-confirm` (the dialog), `-consequences`, `-cancel`, `-confirm-button`, `-error`, `-close`.

import { useState } from 'react';
import { Button, Card, CardBody, Dialog, ErrorMessage, testIdProp } from '@givmo/design-system';
import { isLastGlobalAdminRemovalRefusal } from './org-access-model';
import type { OrgConsoleConfig } from './org-console-config';
import { GLOBAL_ADMIN_LABEL } from './org-tiers';
import { useLeaveOrganization } from './use-org-console';

export const ORG_LEAVE_TEST_ID = 'org-leave';
const PREFIX = ORG_LEAVE_TEST_ID;

/** The control's word on the card (the *Deactivate organization* card's twin). */
export const LEAVE_CONTROL_LABEL = 'Leave organization';
/** The dialog's destructive confirm — the act and its consequence in one phrase. */
export const LEAVE_CONFIRM_LABEL = 'Leave and sign out';

// ⚠ "A NEW INVITATION", NEVER WHO SENDS IT: a Global Admin can invite them back to anything, and an
// app's own inviters (a holder of `<module>:invite_admins`) back into that app — the way back is the
// invitation, whoever holds the door.

/**
 * The card's one line, then the host's note where it writes one — what leaving does not touch, in its
 * class's words (`vocab.leaveNote`, D-CPO-69 question 15).
 */
export function leaveCardBody(note?: string): string {
  const line =
    'Takes you off the team and signs you out. You’d need a new invitation to come back.';
  const added = (note ?? '').trim();
  return added.length > 0 ? `${line} ${added}` : line;
}

/** The dialog's consequence, naming the organization. */
export function leaveConsequence(orgName: string): string {
  return (
    `You’ll lose access to every app in ${orgName} right away, and you’ll be signed out. To come ` +
    'back, you’d need a new invitation.'
  );
}

/** The last Global Admin's refusal. */
export function leaveLastGlobalAdminRefusal(orgName: string): string {
  return `You’re the only ${GLOBAL_ADMIN_LABEL} of ${orgName}. Make someone else a ${GLOBAL_ADMIN_LABEL} before you leave.`;
}

export interface OrgLeaveCardProps {
  config: OrgConsoleConfig;
  /** The host's sign-out and door hop, once the person is off the team. */
  onLeft: () => void;
}

export function OrgLeaveCard({ config, onLeft }: OrgLeaveCardProps) {
  const [open, setOpen] = useState(false);
  const selfUserId = (config.selfUserId ?? '').trim();
  const orgName = (config.vocab.orgName ?? '').trim();
  if (selfUserId.length === 0 || orgName.length === 0) return null;
  return (
    <>
      <Card testId={`${PREFIX}-card`}>
        <CardBody className="flex flex-col gap-12 sm:flex-row sm:items-center sm:justify-between sm:gap-16">
          <div className="flex min-w-0 flex-col gap-4">
            <h3 className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              Leave {orgName}
            </h3>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {leaveCardBody(config.vocab.leaveNote)}
            </p>
          </div>
          <Button
            appearance="saas"
            tone="secondary"
            size="md"
            className="shrink-0 self-start sm:self-auto"
            onClick={() => setOpen(true)}
            testId={`${PREFIX}-open`}
          >
            {LEAVE_CONTROL_LABEL}
          </Button>
        </CardBody>
      </Card>
      <OrgLeaveDialog
        config={config}
        orgName={orgName}
        selfUserId={selfUserId}
        open={open}
        onOpenChange={setOpen}
        onLeft={onLeft}
      />
    </>
  );
}

interface OrgLeaveDialogProps {
  config: OrgConsoleConfig;
  orgName: string;
  selfUserId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLeft: () => void;
}

function OrgLeaveDialog({
  config,
  orgName,
  selfUserId,
  open,
  onOpenChange,
  onLeft,
}: OrgLeaveDialogProps) {
  const leave = useLeaveOrganization(config.orgRef);
  const [error, setError] = useState<string | null>(null);
  const [left, setLeft] = useState(false);
  // The only Global Admin's refusal: the one answer pressing again cannot change.
  const [refused, setRefused] = useState(false);
  const busy = leave.isPending || left;

  const confirm = () => {
    setError(null);
    leave.mutate(
      { selfUserId },
      {
        onSuccess: () => {
          // Off the team: the host's sign-out takes the person to its door. The confirm stays busy
          // until it does — there is nothing here to go back to.
          setLeft(true);
          onLeft();
        },
        onError: (thrown: unknown) => {
          const onlyGlobalAdmin = isLastGlobalAdminRemovalRefusal(thrown);
          setRefused(onlyGlobalAdmin);
          setError(
            onlyGlobalAdmin
              ? leaveLastGlobalAdminRefusal(orgName)
              : 'Something went wrong. Please try again.',
          );
        },
      },
    );
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        if (next) {
          // A fresh look each time: by then someone else may be a Global Admin.
          setError(null);
          setRefused(false);
        }
        onOpenChange(next);
      }}
    >
      <Dialog.Content
        aria-label={`Leave ${orgName}`}
        className="flex flex-col gap-16"
        testId={`${PREFIX}-confirm`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Leave {orgName}?
        </Dialog.Title>
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
          {...testIdProp(`${PREFIX}-consequences`)}
        >
          {leaveConsequence(orgName)}
        </p>
        {/* An outstanding invitation is untouched by leaving (the removal leaves invites alone): the
            one way back in this person can act on, so it is worth its sentence. */}
        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
          Any outstanding invitation to your address still works.
        </p>
        {error !== null && <ErrorMessage testId={`${PREFIX}-error`}>{error}</ErrorMessage>}
        <div className="mt-8 flex items-center justify-end gap-8">
          {refused ? (
            <Button
              appearance="saas"
              tone="secondary"
              size="md"
              onClick={() => onOpenChange(false)}
              testId={`${PREFIX}-close`}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                appearance="saas"
                tone="secondary"
                size="md"
                disabled={busy}
                onClick={() => onOpenChange(false)}
                testId={`${PREFIX}-cancel`}
              >
                Cancel
              </Button>
              <Button
                appearance="saas"
                tone="danger"
                size="md"
                loading={busy}
                onClick={confirm}
                testId={`${PREFIX}-confirm-button`}
              >
                {LEAVE_CONFIRM_LABEL}
              </Button>
            </>
          )}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
